'use client';

/**
 * Leads board row — the identity cluster's signals (LF-5986 / LEADS-19).
 *
 * **Every signal answers a question no other signal on the row answers**
 * (`functional-spec.md` §2.1, §3.5). That discipline is why several things a
 * board like this usually carries are deliberately absent: no `Ready to book`
 * badge (it restated the green `100%` two cells away, and survives as a sort
 * key only), no unread count, no day count beside `Awaiting …` (the Age column
 * already answers "does this need attention").
 *
 * **The colour grammar keys on _who owes the next action_** (§3.6), not on
 * progress:
 *
 * | Treatment | Means | Worn by |
 * |---|---|---|
 * | amber pill | your move, now | `Pending cost agreement` — the only one |
 * | green pill | a gate cleared | `Ready to create matter` — the only one |
 * | blue dot + text | something new to **read** | `Unread reply` |
 * | plain grey text | a passive wait on the client | `Awaiting …` |
 * | slate-700 text | a recorded fact on a closed lead | the closed reason |
 * | amber wash + flag | she marked this by hand | a flagged lead |
 * | square chip | an identifier, not a state | the matter reference |
 *
 * Purple appears once, on the Scribe glyph, because purple is reserved app-wide
 * for Vale AI and Scribe genuinely is.
 */

import { createElement } from 'react';
import {
  AudioLinesIcon,
  FlagIcon,
  HashIcon,
  HourglassIcon
} from 'lucide-react';
import { getMatterTypeIcon } from '@/components/quick-actions/matter-types-view';
import { StatusBadge } from '@/components/ui/status-badge';
import { cn } from '@/lib/utils';
import type { LeadBoardRow } from '@/lib/services/leads';
import {
  LEAD_COST_AGREEMENT_LABELS,
  LEAD_NOT_PROCEEDING_REASON_LABELS,
  LEAD_OUTSTANDING_LABELS,
  LEAD_OUTSTANDING_SHORT_LABELS
} from './lead-labels';
import { LEAD_FLAG_SLOT } from './lead-columns';
import { formatSpelledDate, secondaryNoteParts } from './lead-row-format';

/**
 * The row's pills need an edge, and `StatusBadge`'s `bordered` emphasis is it.
 *
 * **Measured, not styled by eye.** The retired `StatusPill` was deliberately
 * borderless — a pastel `--banner-*` fill and no ring — which is right on a
 * white card but leaves the fill doing all the work of saying "this is a pill".
 * Sampled in the browser, the `caution` fill (`#fef3c7`) measured **1.07:1**
 * against a flagged row's `amber-50` wash and 1.11:1 against plain white, so on
 * a flagged row the pill lost its shape entirely — the one thing §3.5.9 said had
 * to survive the wash ("`Pending cost agreement`'s pill needs to remain
 * distinguishable from its own background"). The fix was a hand-rolled
 * `ring-1 ring-amber-300/60` bolted on at the call site, because [LEADS-10] had
 * ruled the primitive itself stays borderless.
 *
 * **LF-6156 folds that ring into the design system.** `StatusBadge`'s default
 * `bordered` emphasis *is* the bordered-list idiom the ring was approximating
 * (`border-amber-200 bg-amber-50` / `border-green-200 bg-green-50`), so the
 * per-call-site ring classes are gone and the edge now comes from the token.
 * Note the consequence on a flagged row: `caution`'s `bg-amber-50` fill is now
 * the same amber-50 as the wash, so the border alone carries the shape — which
 * is exactly what §3.5.9 asked for, and strictly more edge than the 1.07:1 fill
 * ever gave. `subtle` would have re-created the original defect (its
 * `bg-amber-100` is that same `#fef3c7`), so `bordered` is the load-bearing
 * choice here, not merely the default.
 */
const PILL_BASE = 'shrink-0 whitespace-nowrap';

/**
 * §3.5.9 — the flag: the board's one manual mark, left-most and **outside** the
 * identity cluster, because it is a property of the row rather than another
 * fact qualifying the lead (Outlook's and Jira's placement).
 *
 * **Always visible, light grey at rest** — not hover-revealed. An affordance
 * nobody can see is an affordance nobody uses, and a hover-only control is
 * invisible in every screenshot of the board.
 *
 * **A closed lead renders a spacer, not a control**: there is nothing left to
 * come back to, and a control that cannot do anything should not render.
 */
export function LeadFlagCell({
  row,
  onToggle
}: {
  row: Pick<LeadBoardRow, 'flagged' | 'outcome'>;
  onToggle?: () => void;
}) {
  // The band labels reserve this exact footprint, so a closed row's spacer must
  // be the same slot rather than a hand-written width.
  if (row.outcome) return <span className={LEAD_FLAG_SLOT} aria-hidden />;

  const flagged = row.flagged;
  const glyph = (
    <FlagIcon className={cn('h-3.5 w-3.5', flagged && 'fill-warning')} />
  );
  /**
   * **`-m-2 p-2` grows the hit target without moving anything.** The glyph's own
   * box measures 11.89px — this app scales `--spacing` to ~0.854x — which is
   * under half of WCAG 2.5.8's 24px minimum, and a click a pixel off it fell
   * through to the row and opened the drawer instead. The padding takes the
   * target to ~25px; the equal negative margin leaves the layout footprint
   * unchanged, so the extra area is spent on the row's own left padding and the
   * dead gap before the (non-interactive) type icon.
   */
  const tone = flagged
    ? 'text-warning hover:text-warning-subtle-foreground'
    : 'text-foreground-subtle hover:text-warning';

  /*
    No handler supplied — the mark still renders as **state**, but as a plain
    glyph rather than a button that would do nothing.

    This branch is a real case (a read-only host), not a placeholder, and that
    is exactly what made it dangerous: the board shipped without wiring
    `onToggle`, so every row fell here and the flag looked correct while
    swallowing clicks into the row underneath. If you are here because the flag
    will not toggle, the fault is in the **host**, not in this cell.
  */
  if (!onToggle) {
    return (
      <span
        aria-label={flagged ? 'Flagged' : undefined}
        title={flagged ? 'Flagged to come back to' : undefined}
        className={cn(
          '-m-2 shrink-0 p-2',
          flagged ? 'text-warning' : 'text-foreground-subtle'
        )}
      >
        {glyph}
      </span>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={flagged}
      aria-label={flagged ? 'Remove flag' : 'Flag this enquiry'}
      title={flagged ? 'Remove flag' : 'Flag this enquiry to come back to it'}
      onClick={(e) => {
        // The row itself opens the drawer; flagging must not.
        e.stopPropagation();
        onToggle();
      }}
      className={cn('-m-2 shrink-0 cursor-pointer p-2 transition-colors', tone)}
    >
      {glyph}
    </button>
  );
}

/**
 * §3.5.2 — the enquiry's matter-type glyph, reusing the matter list's own
 * `getMatterTypeIcon` so a probate lead and a probate matter carry the same
 * mark. It is also the only thing on the row revealing that an enquiry is *not*
 * a deceased estate (an estate plan, a dispute).
 *
 * `h-5 w-5 text-[#a3a6a8]` is **matched to `compact-matter-card`'s flag-on
 * branch, not chosen**: a mark at a different size is only half the same mark.
 */
export function LeadTypeIcon({
  row
}: {
  row: Pick<LeadBoardRow, 'enquiryType'>;
}) {
  return (
    // Wrapped rather than passing `title` to the icon — Lucide's props do not
    // include it, and the tooltip is the only place the row names the type.
    <span
      title={row.enquiryType}
      aria-label={row.enquiryType}
      className="inline-flex shrink-0"
    >
      {createElement(getMatterTypeIcon(row.enquiryType), {
        className: 'h-5 w-5 text-foreground-subtle'
      })}
    </span>
  );
}

/**
 * §3.5.3 — the linked matter's reference. **Square, because a square is an
 * identifier and never a state** — the one shape rule that keeps this
 * distinguishable from the row's pills. A lead carrying it while still in intake
 * is the lead/matter decoupling made visible.
 */
export function LeadMatterChip({ reference }: { reference: string }) {
  return (
    <span
      title={`Linked matter ${reference}`}
      className="text-12px border-border bg-surface-subtle text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 font-medium"
    >
      <HashIcon className="text-foreground-subtle h-2.5 w-2.5" />
      {reference}
    </span>
  );
}

/**
 * §3.5.4 — an unread reply from the enquirer: **blue dot + blue text, no pill,
 * no count, no animation.**
 *
 * Blue because blue is what this app already means by *unread* (production's
 * conversation list marks an unread thread with a blue dot and blue text), and
 * because it lands outside the who-owes-what axis entirely: a message arriving
 * is not the same claim as an answer being owed, so it must not borrow amber's
 * weight.
 *
 * **Static deliberately.** This state can sit for days, and motion means
 * *transient*; a permanent animation is noise, fights
 * `prefers-reduced-motion`, and runs into WCAG 2.2.2.
 *
 * **No count** — what the server derives is a boolean, and a number would imply
 * per-message read tracking that does not exist.
 */
export function LeadUnreadReplySignal({
  row
}: {
  row: Pick<LeadBoardRow, 'hasUnseenReply' | 'clientNames'>;
}) {
  if (!row.hasUnseenReply) return null;
  return (
    <UnseenMarker
      title={`${row.clientNames} sent a message that nobody has read yet`}
      dotLabel="Unread message"
      label="Unread reply"
    />
  );
}

/**
 * The client added documents to the submitted intake form and nobody has looked
 * at the enquiry's documents since (VALE-2012). Cleared by the Documents card.
 *
 * **The unread reply's treatment, with its own words.** Both say the same kind of
 * thing — something new arrived from the client and nobody has seen it — and blue
 * already means *unseen* on this board, so a second colour would invent a
 * distinction the reader has no use for. The label is what differs, because what
 * the coordinator does next differs: read a message, or look at documents.
 */
export function LeadNewDocumentsSignal({
  row
}: {
  row: Pick<LeadBoardRow, 'hasUnseenClientDocuments' | 'clientNames'>;
}) {
  if (!row.hasUnseenClientDocuments) return null;
  return (
    <UnseenMarker
      title={`${row.clientNames} added documents to the intake form that nobody has looked at yet`}
      dotLabel="New documents"
      label="New documents"
    />
  );
}

/** The blue dot and blue label both unseen signals share. */
function UnseenMarker({
  title,
  dotLabel,
  label
}: {
  title: string;
  dotLabel: string;
  label: string;
}) {
  return (
    <span
      title={title}
      className="text-12px text-info-subtle-foreground inline-flex shrink-0 items-center gap-1.5 font-medium whitespace-nowrap"
    >
      {/*
        `bg-blue-500` on the dot against `text-blue-600` on the label —
        production's own pairing, where the dot is a shade stronger than the
        text beside it.

        **`-top-px` is a measured optical correction, not an eyeball.**
        `items-center` centres the dot on the 18px line box, which reserves
        descender space for the `p` and `y` in "reply"; the eye centres on the
        cap/x-height band, which sits higher. The dot's centre measured 0.49px
        low — a sub-pixel offset that rounds to a whole device pixel and so
        reads as clearly sitting low. Do not "fix" this back to pure centring.
      */}
      <span
        aria-label={dotLabel}
        className="bg-info relative -top-px h-2 w-2 shrink-0 rounded-full"
      />
      {label}
    </span>
  );
}

/**
 * §3.5.5 — what the firm is still waiting on **from the client**: the
 * longest-standing item only, with `+N` for the rest.
 *
 * **Plain grey, never a pill**: the client owes this, so it reads as a passive
 * wait rather than something the firm must act on — amber is reserved for her
 * move. Advisory, never gating; it explains why a lead is slow, it does not
 * hold a stage back.
 *
 * **No day count on the row.** The Age column already answers "does this need
 * attention", and a second day count inches from the first reads as
 * duplication. How long since we asked is in the tooltip, where the answer is
 * actually used.
 */
export function LeadOutstandingSignal({
  row
}: {
  row: Pick<LeadBoardRow, 'outstanding' | 'outstandingCount'>;
}) {
  const item = row.outstanding;
  if (!item) return null;
  const extra = Math.max(row.outstandingCount - 1, 0);

  return (
    <span
      title={[
        `Waiting on the ${LEAD_OUTSTANDING_LABELS[item.type]}, asked ${formatSpelledDate(item.requestedAt)}`,
        item.note,
        extra > 0 ? `+${extra} more outstanding` : null
      ]
        .filter(Boolean)
        .join('\n')}
      className="text-12px text-muted-foreground inline-flex shrink-0 items-center gap-1 whitespace-nowrap"
    >
      <HourglassIcon className="h-3 w-3" />
      Awaiting {LEAD_OUTSTANDING_SHORT_LABELS[item.type]}
      {extra > 0 && <span className="opacity-70">+{extra}</span>}
    </span>
  );
}

/**
 * §3.5.6 — where the cost agreement stands. **Only in `Pending engagement`, and
 * then always**, gated on the server's `section` so it cannot leak onto a lead
 * that has not met.
 *
 * The pill-vs-plain split is the grammar doing its work: `Pending cost
 * agreement` (nothing drafted — the firm's move) and `Ready to create matter`
 * (the retainer is paid — the firm's move again) are **pills**, while a
 * signature and a fee are owed by the *client* and stay plain grey with an
 * hourglass.
 *
 * Both pills use the shared `StatusBadge` — `caution` for the amber rung and
 * `success` for the green. This board would otherwise have been the fourth
 * surface to hand-assemble the outline-`Badge` formula, which is the finding
 * that put those rungs on the primitive in the first place.
 *
 * **A lead that moves to `Pending allocation` correctly loses this signal.** The
 * band means *somebody handed this over and nobody has picked it up yet*, so
 * every row in it needs the same thing from the same person — take it on — and a
 * cost-agreement pill would offer the reader an errand that is not theirs while
 * they scan their queue. It is the same judgement `byWaitingLongest` makes by
 * having no second sort key at all.
 *
 * On a *converted* row it is doubly right: `Ready to create matter` would claim
 * an errand the matter chip two cells away proves is already done. Keying on the
 * server's `section` rather than on `costAgreementState` alone is what makes both
 * cases fall out for free — including, since VALE-1401, the handed-over rows that
 * are not converted at all.
 */
export function LeadCostAgreementSignal({
  row
}: {
  row: Pick<LeadBoardRow, 'section' | 'costAgreementState'>;
}) {
  if (row.section !== 'pending_engagement') return null;

  const state = row.costAgreementState;
  const label = LEAD_COST_AGREEMENT_LABELS[state];

  if (state === 'none') {
    return (
      <StatusBadge
        tone="caution"
        title="No cost agreement drafted yet"
        className={PILL_BASE}
      >
        {label}
      </StatusBadge>
    );
  }

  if (state === 'paid') {
    return (
      <StatusBadge
        tone="success"
        title="Initial fee received, ready to convert to a matter"
        className={PILL_BASE}
      >
        {label}
      </StatusBadge>
    );
  }

  return (
    <span
      title={
        state === 'awaiting_signature'
          ? 'Cost agreement sent for signature'
          : 'Cost agreement signed; the initial fee has not been recorded'
      }
      className="text-12px text-muted-foreground inline-flex shrink-0 items-center gap-1 whitespace-nowrap"
    >
      <HourglassIcon className="h-3 w-3" />
      {label}
    </span>
  );
}

/**
 * §3.5.7 — a Scribe recording exists. **The only purple on the board besides
 * the AI cards**, because purple is reserved app-wide for Vale AI.
 */
export function LeadScribeGlyph() {
  return (
    <span
      title="First meeting recorded with Scribe"
      aria-label="Meeting recorded with Scribe"
      className="text-ai/80 inline-flex shrink-0 items-center"
    >
      <AudioLinesIcon className="h-3.5 w-3.5" />
    </span>
  );
}

/**
 * §3.5.8 — the not-proceeding reason, on closed rows only.
 *
 * **`slate-700` + `font-medium`, not rose.** Colour on this row means *who owes
 * the next action*, and a closed lead owes nobody anything — rose spent the
 * board's one actionability signal on the only row type with no action, while
 * implying the outcome was a failure. Plenty are not: `No estate to administer`
 * and `Not proceeding yet` are ordinary results.
 *
 * It cannot be plain slate at the secondary note's weight either, or the label
 * reads as the first words of the source line beside it. It wins on darkness
 * and weight instead — which is also the right hierarchy, since on a closed
 * lead the reason is the single most important fact.
 *
 * **The shade is chosen by measurement**: closed rows carry `opacity-75`, which
 * drags everything on them down. `slate-600` measured 4.03:1 on white with that
 * opacity folded in — under WCAG AA's 4.5:1 for small text. `slate-700` clears
 * it.
 */
export function LeadClosedReason({
  row
}: {
  row: Pick<LeadBoardRow, 'outcome' | 'notProceedingReason'>;
}) {
  if (row.outcome !== 'not_proceeding' || !row.notProceedingReason) return null;
  const label = LEAD_NOT_PROCEEDING_REASON_LABELS[row.notProceedingReason];
  return (
    <span
      title={`Not proceeding: ${label}`}
      className="text-12px text-foreground-secondary inline-flex min-w-0 shrink items-center truncate font-medium"
    >
      {label}
    </span>
  );
}

/**
 * §3.5.14 — the grey secondary note. One truncating text block rather than a
 * flex row, so a long referral type ellipsises properly instead of being
 * hard-clipped.
 *
 * **`flex-1` (i.e. `flex: 1 1 0%`): the note gives up *all* of its space before
 * the title loses a single pixel.** At the default `shrink` it and the title were
 * both `truncate`d flex siblings at `flex-shrink: 1`, so they shrank in lockstep
 * and a narrow row ended up with two half-clipped strings and no readable
 * identifier — one observed row rendered `Estat…` while 40 characters of grey note
 * survived beside it. That is inverted priority: the note is context, the title is
 * *which lead this is*, and a board is read by scanning titles.
 *
 * **The zero flex-basis is the whole mechanism, and a weighted `flex-shrink` is
 * not a substitute.** Flexbox distributes overflow in proportion to
 * `flex-shrink × flex-base-size`, so a base size of `0` gives this note a shrink
 * weight of zero: it cannot take width from the title, and instead *grows* into
 * whatever the title and signals leave behind. The title therefore keeps its full
 * intrinsic width until the row can no longer fit it at all, at which point the
 * note is already at zero and the title truncates as the genuine last resort.
 *
 * A weighted shrink (`shrink-[100]`, as the inventory row's detail span uses —
 * `balance-sheet/components/inventory-item-row.tsx`, LF-3592) was tried here first
 * and is **not** sufficient, which is worth recording because it looks like it
 * should be. Proportional distribution can only ever make the title's share
 * small, never zero: measured on this board it cost the title 1–2px at every
 * viewport width. That reads as a rounding error and is not one — `text-overflow:
 * ellipsis` has to fit the `…` glyph inside the box, so a 2px deficit drops
 * several *characters*. "Nearly no shrink" and "no shrink" are visually different
 * outcomes here, so this row deliberately diverges from the inventory pattern.
 *
 * It matters most where the identity cluster carries an *unshrinkable* chip: the
 * matter reference is `shrink-0` (a truncated identifier is worse than none), so
 * on a `Pending allocation` row the chip's width came straight out of the title.
 * With the note yielding first the chip is paid for out of context, not identity.
 *
 * Growing is safe because this note is always the **last** child of the identity
 * cluster, so filling the remainder pushes nothing; the text is left-aligned, so a
 * short note renders exactly where it did before, in a wider box.
 */
export function LeadSecondaryNote({
  row
}: {
  row: Pick<
    LeadBoardRow,
    'businessSource' | 'referredBy' | 'netAssetValueCents' | 'moreInfo'
  >;
}) {
  const parts = secondaryNoteParts(row);
  if (parts.length === 0) return null;
  const text = parts.join(' · ');
  return (
    <span
      title={text}
      className="text-12px text-muted-foreground block min-w-0 flex-1 truncate"
    >
      {text}
    </span>
  );
}
